import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const BASE = '/markup';

/**
 * Map API product rule to UI format
 * API: { id, rule_name, product_type, markup_type, markup_value, start_date, end_date, status }
 * UI:  { id, name, productType, markupType, markupValue, startDate, endDate, status }
 */
const toDateStr = (v) => {
  if (!v) return '';
  if (typeof v === 'string') return v.split('T')[0];
  if (v instanceof Date) return v.toISOString().split('T')[0];
  return '';
};

const mapApiProductToUi = (api) => {
  if (!api) return null;
  const markupValue = api.markup_value
    ? parseFloat(String(api.markup_value).replace(/[%€]/g, '')) || 0
    : 0;
  return {
    id: api.id,
    name: api.rule_name ?? '',
    productType: api.product_type ? api.product_type.charAt(0).toUpperCase() + api.product_type.slice(1) : '',
    markupType: api.markup_type === 'fixed_amount' ? 'Fixed' : 'Percentage',
    markupValue,
    startDate: toDateStr(api.start_date),
    endDate: toDateStr(api.end_date),
    status: api.status ?? true,
  };
};

/**
 * Map API supplier rule to UI format
 */
const mapApiSupplierToUi = (api) => {
  if (!api) return null;
  const markupValue = api.markup_value
    ? parseFloat(String(api.markup_value).replace(/[%€]/g, '')) || 0
    : 0;
  return {
    id: api.id,
    name: api.rule_name ?? '',
    supplier: api.supplier ?? '',
    markupType: api.markup_type === 'fixed_amount' ? 'Fixed' : 'Percentage',
    markupValue,
    startDate: toDateStr(api.start_date),
    endDate: toDateStr(api.end_date),
    status: api.status ?? true,
  };
};

/**
 * Map UI form to API format for create/update
 */
export const mapUiToApi = (ui, ruleType) => {
  const productTypeMap = { Flights: 'flights', Hotels: 'hotels', Insurance: 'insurance', Packages: 'packages' };
  const payload = {
    rule_type: ruleType === 'product' ? 'PRODUCT' : 'SUPPLIER',
    rule_name: ui.name?.trim() ?? '',
    markup_type: ui.markupType === 'Fixed' ? 'fixed_amount' : 'percentage',
    markup_value: parseFloat(ui.markupValue) || 0,
    start_date: ui.startDate || null,
    end_date: ui.endDate?.trim() || null,
    status: ui.status ?? true,
  };
  if (ruleType === 'product') {
    payload.product_type = productTypeMap[ui.productType] || (ui.productType?.toLowerCase() ?? 'flights');
  } else {
    payload.supplier = ui.supplier?.trim() ?? '';
  }
  return payload;
};

/**
 * GET Product Rules
 */
export function useProductRules(params = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { limit: 100 };
      if (params.q) queryParams.q = params.q;
      if (params.product_type) queryParams.product_type = params.product_type;
      if (params.status !== undefined && params.status !== '') queryParams.status = params.status;

      const { data: res } = await axiosInstance.get(`${BASE}/products`, { params: queryParams });
      const list = res?.data ?? [];
      setData(list.map(mapApiProductToUi));
      return list;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load product rules';
      setError(msg);
      setData([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.q, params.product_type, params.status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}

/**
 * GET Supplier Rules
 */
export function useSupplierRules(params = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = { limit: 100 };
      if (params.q) queryParams.q = params.q;
      if (params.markup_type) queryParams.markup_type = params.markup_type;
      if (params.status !== undefined && params.status !== '') queryParams.status = params.status;

      const { data: res } = await axiosInstance.get(`${BASE}/suppliers`, { params: queryParams });
      const list = res?.data ?? [];
      setData(list.map(mapApiSupplierToUi));
      return list;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load supplier rules';
      setError(msg);
      setData([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [params.q, params.markup_type, params.status]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}

/**
 * GET Markup Data Count (for stats)
 */
export function useMarkupCount() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/count`);
      setData(res?.data ?? null);
      return res?.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load markup count';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  return { data, loading, error, refetch: fetchCount };
}

/**
 * GET Priority Conflicts
 */
export function usePriorityConflicts() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${BASE}/priority-conflicts`);
      setData(res?.data ?? []);
      return res?.data ?? [];
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load priority conflicts';
      setError(msg);
      setData([]);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, loading, error, refetch: fetchList };
}

/**
 * CREATE Markup Rule
 */
export function useCreateMarkupRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload, ruleType) => {
    setLoading(true);
    setError(null);
    try {
      const apiPayload = mapUiToApi(payload, ruleType);
      const { data: res } = await axiosInstance.post(BASE, apiPayload);
      const created = res?.data;
      if (ruleType === 'product') {
        return { success: true, data: mapApiProductToUi(created) };
      }
      return { success: true, data: mapApiSupplierToUi(created) };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to create rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * UPDATE Markup Rule
 */
export function useUpdateMarkupRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload, ruleType) => {
    if (!id) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      const apiPayload = mapUiToApi(payload, ruleType);
      const { data: res } = await axiosInstance.put(`${BASE}/${id}`, apiPayload);
      const updated = res?.data;
      if (ruleType === 'product') {
        return { success: true, data: mapApiProductToUi(updated) };
      }
      return { success: true, data: mapApiSupplierToUi(updated) };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to update rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * DELETE Markup Rule
 */
export function useDeleteMarkupRule() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${BASE}/${id}`);
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Failed to delete markup rule';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * UPDATE Markup Rule Status (only status field)
 */
export function useUpdateMarkupStatus() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, status) => {
    if (!id) throw new Error('Rule id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.put(`${BASE}/${id}`, { status });
      return { success: true };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

/**
 * UPDATE Priority Conflict
 */
export function useUpdatePriorityConflict() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (conflictId, status) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(`${BASE}/priority-conflicts/${conflictId}`, { status });
      return { success: true, data: res?.data };
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update priority conflict';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}
